import { Box, Flex, VStack, useDisclosure } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import StudentsPanel from "./StudentsPanel";
import ResultsEntryPanel from "./ResultsEntryPanel";
import { useStudentsContext } from "../../../Contexts/StudentsContext";
import {
  ApiDataResponse,
  ApiErrorResponse,
  ApiResponse,
  BehavioralResultEntry,
  BehavioralSubject,
  ClassResults,
  CognitiveResultEntry,
  Student,
  Subject,
  SummaryResultEntry,
  BulkImportResult,
} from "../../../services/Dtos";
import useUnsavedChangesPrompt from "../../../hooks/useUnsavedChangesPrompt";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import useToaster from "../../../hooks/useToaster";
import axios from "axios";
import { isNullOrEmpty } from "../../../services/Utils";
import LogoLoader from "../../CustomUi/LogoLoader";
import useAxios from "../../../hooks/useAxios";
import FileUploadModal from "../../CustomUi/FileUploadModal";
import BulkImportFeedbackModal from "../../CustomUi/BulkImportFeedbackModal";
import TemplateCustomizationModal from "./TemplateCustomizationModal";

function Results() {
  const axiosClient = useAxios();

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [currentLevel, setCurrentLevel] = useState("");
  const [fetchingPdf, setFetchingPdf] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isActionLoading] = useState(false);
  const [isTemplateCustomizationOpen, setIsTemplateCustomizationOpen] =
    useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [importFeedback, setImportFeedback] = useState<BulkImportResult | null>(
    null
  );

  const [resultEntries, setResultEntries] = useState<CognitiveResultEntry[]>(
    []
  );

  const [behavioralResultEntries, setBehavioralResultEntries] = useState<
    BehavioralResultEntry[]
  >([]);

  const [summaryResultEntry, setSummaryResultEntry] =
    useState<SummaryResultEntry>({} as SummaryResultEntry);

  const [classResultEntries, setClassResultEntries] = useState<
    CognitiveResultEntry[]
  >([]);

  const [classBehavioralResultEntries, setClassBehavioralResultEntries] =
    useState<BehavioralResultEntry[]>([]);

  const [classSummaryResultEntries, setClassSummaryResultEntries] = useState<
    SummaryResultEntry[]
  >([]);

  const [editedCognitiveEntries, setEditedCognitiveEntries] = useState<
    CognitiveResultEntry[]
  >([]);
  const [editedBehavioralEntries, setEditedBehavioralEntries] = useState<
    BehavioralResultEntry[]
  >([]);

  const { school, sessions, terms, assessmentTypes, gradeCriterias, classes } =
    useSchoolsContext();
  const [fetchingClassResult, setFetchingClassResult] = useState(false);
  const [savingStudentResultEntries, setSavingStudentResultEntries] =
    useState(false);

  const [editedSummaryEntry, setEditedSummaryEntry] =
    useState<SummaryResultEntry>({} as SummaryResultEntry);
  const [currentClassSubjects, setCurrentClassSubjects] = useState<Subject[]>(
    []
  );
  const [currentBehavioralSubjects, setCurrentBehavioralSubjects] = useState<
    BehavioralSubject[]
  >([]);
  const [selectedClassId, setSelectedClassId] = useState(() => {
    return localStorage.getItem("selectedClassId") || "";
  });

  const { students } = useStudentsContext();

  const [chosenStudent, setChosenStudent] = useState<Student | null>(() => {
    const storedStudentId = localStorage.getItem("chosenStudentId");
    if (storedStudentId) {
      const student = students.find((s) => s.id === storedStudentId);
      // Only set the student if they belong to the selected class
      if (
        student &&
        student.currentClassId ===
          (localStorage.getItem("selectedClassId") || "")
      ) {
        return student;
      }
    }
    return null;
  });

  const [editingSubjectIds, setEditingSubjectIds] = useState<string[]>([]);

  const { showSuccessToast, showErrorToast } = useToaster();

  const filteredAssessmentTypes = assessmentTypes.filter(
    (type) => type.levelId === currentLevel
  );

  const filteredGradeCriterias = gradeCriterias.filter(
    (criteria) => criteria.levelId === currentLevel
  );

  const isValidResult = (): boolean => {
    return (
      !isNullOrEmpty(school?.currentSessionId) &&
      !isNullOrEmpty(school?.currentTermId) &&
      filteredAssessmentTypes.length !== 0 &&
      filteredGradeCriterias.length !== 0
    );
  };

  useEffect(() => {
    let isMounted = true;
    setResultEntries([]);
    setBehavioralResultEntries([]);
    setSummaryResultEntry({} as SummaryResultEntry);
    const selectedClass = classes.find((c) => c.id === selectedClassId);
    if (selectedClass) {
      setCurrentClassSubjects(selectedClass.subjects);
      setCurrentBehavioralSubjects(selectedClass.behavioralSubjects);
      setCurrentLevel(selectedClass.levelId);

      const fetchResults = async () => {
        try {
          setFetchingClassResult(true);

          const response = await axiosClient.get<ApiDataResponse<ClassResults>>(
            `classes/getclasscurrentcbsresults/${selectedClassId}`
          );
          if (isMounted) {
            if (response.data.succeeded) {
              setClassResultEntries(response.data.data.cognitiveResults);
              setClassBehavioralResultEntries(
                response.data.data.behavioralResults
              );
              setClassSummaryResultEntries(response.data.data.summaryResults);
              setEditingSubjectIds([]);
              setUnsavedChanges(false);
            } else {
              showErrorToast();
            }
          }
        } catch (error) {
          if (isMounted) showErrorToast();
        } finally {
          if (isMounted) setFetchingClassResult(false);
        }
      };

      fetchResults();
      return () => {
        isMounted = false;
      };
    } else {
      setCurrentClassSubjects([]);
      setCurrentBehavioralSubjects([]);
    }
  }, [selectedClassId, classes]);

  useEffect(() => {
    setResultEntries(
      classResultEntries.filter((r) => r.studentId === chosenStudent?.id)
    );
    setBehavioralResultEntries(
      classBehavioralResultEntries.filter(
        (r) => r.studentId === chosenStudent?.id
      )
    );
    const summaryEntry = classSummaryResultEntries.find(
      (r) => r.studentId === chosenStudent?.id
    );
    setSummaryResultEntry(summaryEntry ?? ({} as SummaryResultEntry));
  }, [
    chosenStudent,
    classResultEntries,
    classBehavioralResultEntries,
    classSummaryResultEntries,
  ]);

  const currentSession = sessions.find(
    (s) => s.id === school?.currentSessionId
  );

  const currentTerm = terms.find((t) => t.id === school?.currentTermId);

  useUnsavedChangesPrompt(unsavedChanges);

  const handleRowClick = (student: Student | null) => {
    if (unsavedChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      );
      if (!confirmLeave) {
        return;
      }
    }

    // Clear unsaved result entries when switching students
    if (chosenStudent !== student) {
      setEditedCognitiveEntries([]);
      setEditedBehavioralEntries([]);
      setEditedSummaryEntry({} as SummaryResultEntry);
      setEditingSubjectIds([]);
    }

    if (chosenStudent === student) {
      setChosenStudent(null);
      localStorage.removeItem("chosenStudentId");
    } else {
      setChosenStudent(student);
      if (student) {
        localStorage.setItem("chosenStudentId", student.id);
      } else {
        localStorage.removeItem("chosenStudentId");
      }
    }
    setUnsavedChanges(false);
  };

  const handleBackToStudents = () => {
    if (unsavedChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      );
      if (!confirmLeave) {
        return;
      }
    }

    // Clear unsaved result entries when going back to student list
    setEditedCognitiveEntries([]);
    setEditedBehavioralEntries([]);
    setEditedSummaryEntry({} as SummaryResultEntry);
    setEditingSubjectIds([]);

    setChosenStudent(null);
    localStorage.removeItem("chosenStudentId");
    setUnsavedChanges(false);
  };

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<number>(0); // Default to Active

  const handleClassChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (unsavedChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      );
      if (!confirmLeave) {
        return;
      }
    }

    // Clear unsaved result entries when changing classes
    setEditedCognitiveEntries([]);
    setEditedBehavioralEntries([]);
    setEditedSummaryEntry({} as SummaryResultEntry);
    setEditingSubjectIds([]);

    setSelectedClassId(e.target.value);
    setChosenStudent(null);
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setSelectedStatus(Number(e.target.value));
  };

  const filteredStudents = students?.filter((student) => {
    const fullName = `${student.firstName.toLowerCase()} ${student.middleName?.toLowerCase()} ${student.lastName.toLowerCase()}`;
    return (
      fullName.includes(searchQuery.toLowerCase()) &&
      student.currentClassId === selectedClassId &&
      student.status === selectedStatus
    );
  });

  useEffect(() => {
    const saveStates = () => {
      localStorage.setItem("selectedClassId", selectedClassId);
    };

    saveStates();
  }, [selectedClassId]);

  // Clear chosen student when selected class changes
  useEffect(() => {
    if (chosenStudent && chosenStudent.currentClassId !== selectedClassId) {
      setChosenStudent(null);
      localStorage.removeItem("chosenStudentId");
    }
  }, [selectedClassId, chosenStudent]);

  const handleSave = async () => {
    if (!chosenStudent || !selectedClassId) return;

    try {
      setSavingStudentResultEntries(true);

      // Prepare the combined payload
      const combinedPayload = {
        cognitiveResultEntries:
          editedCognitiveEntries.length > 0 ? editedCognitiveEntries : [],
        behavioralResultEntries:
          editedBehavioralEntries.length > 0 ? editedBehavioralEntries : [],
        summaryResultEntry: {
          ...editedSummaryEntry,
          studentId: chosenStudent.id,
          classId: selectedClassId,
          sessionId: school?.currentSessionId,
          termId: school?.currentTermId,
        },
      };

      // Make single API call to the combined endpoint
      const response = await axiosClient.post<ApiResponse>(
        `resultentries/addorupdateresultentries`,
        combinedPayload
      );

      if (response.data.succeeded) {
        showSuccessToast(response.data.message || "Results saved successfully");

        // Refetch class results to ensure data consistency
        await fetchClassResults();
      } else {
        showErrorToast(response.data.message || "Failed to save results");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showErrorToast(
          error.response?.data?.message ??
            "An error occurred while saving results."
        );
      } else {
        showErrorToast("An unexpected error occurred.");
      }
    } finally {
      setSavingStudentResultEntries(false);
      setUnsavedChanges(false);
      setEditedCognitiveEntries([]);
      setEditedBehavioralEntries([]);
      setEditingSubjectIds([]);
    }
  };

  const handlePreview = async () => {
    try {
      setFetchingPdf(true);
      const response = await axiosClient.get(
        `resultreports/getcurrentreportpdf/${chosenStudent?.id}`,
        {
          responseType: "blob",
        }
      );

      // Create blob URL directly from the response
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      // Clean up blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const response = error.response?.data as ApiErrorResponse;
        showErrorToast(response.Message || "");
      } else {
        showErrorToast();
      }
    } finally {
      setFetchingPdf(false);
    }
  };

  const handleBulkUploadSuccess = () => {
    // Refresh the class results after successful bulk upload
    if (selectedClassId) {
      fetchClassResults();
    }
  };

  const handleDownloadTemplate = async () => {
    setIsTemplateCustomizationOpen(true);
  };

  const fetchClassResults = async () => {
    if (!selectedClassId) return;

    try {
      setFetchingClassResult(true);
      const response = await axiosClient.get<ApiDataResponse<ClassResults>>(
        `classes/getclasscurrentcbsresults/${selectedClassId}`
      );

      if (response.data.succeeded) {
        setClassResultEntries(response.data.data.cognitiveResults);
        setClassBehavioralResultEntries(response.data.data.behavioralResults);
        setClassSummaryResultEntries(response.data.data.summaryResults);
        setEditingSubjectIds([]);
        setUnsavedChanges(false);
      } else {
        showErrorToast();
      }
    } catch (error) {
      showErrorToast();
    } finally {
      setFetchingClassResult(false);
    }
  };

  const handleBulkUpload = async (file: File) => {
    setIsBulkUploadOpen(false);
    setIsUploading(true);
    setImportFeedback(null);
    onFeedbackOpen();

    const formData = new FormData();
    formData.append("file", file);
    formData.append("classId", selectedClassId);

    try {
      const response = await axiosClient.post<
        ApiDataResponse<BulkImportResult>
      >("bulkimport/import-results", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.succeeded) {
        setImportFeedback(response.data.data);
        showSuccessToast(
          response.data.message || "Results imported successfully!"
        );
        handleBulkUploadSuccess();
      } else {
        showErrorToast(response.data.message || "Failed to import results.");
      }
    } catch (error) {
      showErrorToast("An error occurred during upload.");
    } finally {
      setIsUploading(false);
    }
  };

  const {
    open: isFeedbackOpen,
    onOpen: onFeedbackOpen,
    onClose: onFeedbackClose,
  } = useDisclosure();

  if (fetchingPdf) {
    return <LogoLoader />;
  }

  return (
    <Box flex={1} h="100%" overflow="hidden">
      <VStack gap={6} h="100%" align="stretch" overflow="hidden">
        {/* Main Content Area */}
        <Flex
          flex={1}
          gap={5}
          direction={{ base: "column", lg: "row" }}
          overflow="hidden"
        >
          {/* Students Panel - Hidden on mobile when student is selected */}
          <Box
            display={{ base: chosenStudent ? "none" : "block", lg: "block" }}
            flex={{ base: 1, lg: 1 }}
            maxW={{ base: "100%", lg: "450px" }}
            overflow="hidden"
          >
            <StudentsPanel
              selectedClassId={selectedClassId}
              students={filteredStudents}
              chosenStudent={chosenStudent}
              handleRowClick={handleRowClick}
              searchQuery={searchQuery}
              handleSearchChange={handleSearchChange}
              selectedStatus={selectedStatus}
              handleStatusChange={handleStatusChange}
              handleClassChange={handleClassChange}
              classes={classes}
              onDownloadTemplate={handleDownloadTemplate}
              onBulkUploadOpen={() => setIsBulkUploadOpen(true)}
              isActionLoading={isActionLoading}
            />
          </Box>

          {/* Results Entry Panel */}
          <Box
            display={{ base: chosenStudent ? "block" : "none", lg: "block" }}
            flex={{ base: 1, lg: 3 }}
            overflow="hidden"
          >
            <ResultsEntryPanel
              chosenStudent={chosenStudent}
              selectedClassId={selectedClassId}
              currentClassSubjects={currentClassSubjects}
              currentBehavioralSubjects={currentBehavioralSubjects}
              editingSubjectIds={editingSubjectIds}
              setEditingSubjectIds={setEditingSubjectIds}
              fetchingClassResult={fetchingClassResult}
              cognitiveResultEntries={resultEntries}
              behavioralResultEntries={behavioralResultEntries}
              editedCognitiveEntries={editedCognitiveEntries}
              editedBehavioralEntries={editedBehavioralEntries}
              setEditedCognitiveEntries={setEditedCognitiveEntries}
              setEditedBehavioralEntries={setEditedBehavioralEntries}
              summaryResultEntry={summaryResultEntry}
              editedSummaryEntry={editedSummaryEntry}
              setEditedSummaryEntry={setEditedSummaryEntry}
              setSummaryResultEntry={setSummaryResultEntry}
              filteredAssessmentTypes={filteredAssessmentTypes}
              filteredGradeCriterias={filteredGradeCriterias}
              currentLevel={currentLevel}
              setUnsavedChanges={setUnsavedChanges}
              unsavedChanges={unsavedChanges}
              currentSessionName={currentSession?.sessionName}
              currentTermName={currentTerm?.termName}
              selectedClassName={
                classes.find((c) => c.id === selectedClassId)?.className
              }
              handleSave={handleSave}
              savingStudentResultEntries={savingStudentResultEntries}
              handlePreview={handlePreview}
              validResult={isValidResult()}
              hasSummaryResultEntries={
                chosenStudent
                  ? classSummaryResultEntries.some(
                      (entry) => entry.studentId === chosenStudent.id
                    )
                  : false
              }
              onBackToStudents={handleBackToStudents}
            />
          </Box>
        </Flex>

        {/* Bulk Results Upload Modal */}
        <FileUploadModal
          isOpen={isBulkUploadOpen}
          onClose={() => setIsBulkUploadOpen(false)}
          onUpload={handleBulkUpload}
          title="Import Results"
          description="Drag & drop or click to select Excel or CSV file"
          isUploading={isUploading}
        />

        {/* Feedback Modal */}
        <BulkImportFeedbackModal
          isOpen={isFeedbackOpen}
          onClose={onFeedbackClose}
          feedback={importFeedback}
          isUploading={isUploading}
          title="Import Results"
          loadingText="Uploading and processing results..."
        />

        {/* Template Customization Modal */}
        <TemplateCustomizationModal
          isOpen={isTemplateCustomizationOpen}
          onClose={() => setIsTemplateCustomizationOpen(false)}
          classes={classes}
          selectedClassId={selectedClassId}
        />
      </VStack>
    </Box>
  );
}

export default Results;
